import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildFeedbackMailto } from '@coparent/shared/src/utils/feedback';
import '../../pages/LandingPage.css';

/**
 * Nav + footer for marketing pages other than the landing page.
 *
 * The landing page keeps its own inline copies because its nav scroll-spies
 * between in-page sections; this version links across routes instead. Both
 * share `LandingPage.css`, so the two stay visually identical without a risky
 * refactor of a 571-line page that is the site's highest-traffic surface.
 *
 * Cross-linking matters here beyond navigation: /features and /pricing exist to
 * catch commercial-intent search traffic, and they need internal links back to
 * the landing page and out to the blog for those queries to consolidate.
 */

const SharedRootsLogo = ({ width = 28, height = 30 }) => (
  <svg width={width} height={height} viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M80 140 L80 95" stroke="#1B6B4A" strokeWidth="7" strokeLinecap="round" />
    <path d="M80 95 Q65 80, 50 65" stroke="#1B6B4A" strokeWidth="6" strokeLinecap="round" fill="none" />
    <path d="M80 95 Q95 80, 110 65" stroke="#1B6B4A" strokeWidth="6" strokeLinecap="round" fill="none" />
    <circle cx="44" cy="50" r="25" fill="#F5C842" fillOpacity="0.85" />
    <circle cx="116" cy="50" r="25" fill="#F2886D" fillOpacity="0.85" />
    <circle cx="80" cy="92" r="10" fill="#3BA676" />
  </svg>
);

export const MarketingNav = () => {
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <nav className="lp-nav">
      <div className="lp-nav-inner">
        <Link to="/" className="lp-nav-logo" aria-label="coparent home">
          <SharedRootsLogo />
          <span className="lp-nav-wordmark">coparent</span>
        </Link>
        <ul className={`lp-nav-links ${mobileNavOpen ? 'is-open' : ''}`}>
          <li><Link className="lp-nav-link-btn" to="/features">Features</Link></li>
          <li><Link className="lp-nav-link-btn" to="/pricing">Pricing</Link></li>
          <li><Link className="lp-nav-link-btn" to="/blog">Blog</Link></li>
          <li><button className="lp-nav-login" onClick={() => navigate('/login')}>Log In</button></li>
          <li><button className="lp-nav-cta" onClick={() => navigate('/register')}>Get Started</button></li>
        </ul>
        <button
          className="lp-nav-mobile-toggle"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileNavOpen}
        >
          <span />
          <span />
        </button>
      </div>
    </nav>
  );
};

export const MarketingFooter = () => {
  const feedbackHref = buildFeedbackMailto({ platform: 'Web', version: 'marketing' });

  return (
    <footer className="lp-footer">
      <div className="lp-container">
        <div className="lp-footer-grid">
          <div>
            <Link to="/" className="lp-nav-logo" aria-label="coparent home">
              <SharedRootsLogo />
              <span className="lp-nav-wordmark">coparent</span>
            </Link>
            <p className="lp-footer-brand-desc">
              Two homes. One plan. Shared calendars, expenses, and custody schedules
              in one calm place.
            </p>
          </div>
          <div>
            <div className="lp-footer-col-title">Product</div>
            <ul className="lp-footer-links">
              <li><Link to="/features">Features</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><Link to="/">Overview</Link></li>
            </ul>
          </div>
          <div>
            <div className="lp-footer-col-title">Company</div>
            <ul className="lp-footer-links">
              <li><Link to="/blog">Blog</Link></li>
              <li><a href={feedbackHref}>Contact</a></li>
            </ul>
          </div>
          <div>
            <div className="lp-footer-col-title">Legal</div>
            <ul className="lp-footer-links">
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span>&copy; 2026 coparent. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
};
